import {
  type Dispatch,
  type FC,
  type RefObject,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import { type RectReadOnly } from 'react-use-measure';
import { motion } from 'framer-motion';

import { generateDetailedArc } from '@/common/lib/generateArcPoints';
import { getImage } from '@/common/lib/images';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';

import dicePng from '/img/dice-game/dice.png';
import flashPng from '/img/dice-game/flash-huge.png';
import cashPng from '/img/icons/cash.png';
import tgemPng from '/img/tgem.png';

const images = {
  dice: dicePng,
  energy: flashPng,
  cash: cashPng,
  tgem: tgemPng,
} as const;

type ImageType = keyof typeof images;
const imageKeys = Object.keys(images);

interface FlyingImg {
  fromRef: RefObject<HTMLElement | null> | null;
  toRef: RefObject<HTMLElement> | null;
  isFlying: boolean;
  setIsFlying?: Dispatch<SetStateAction<boolean>>;
  type: ImageType | ResourceTypeText | PlantTypeText | GoodsType;
  delay?: number;
  completeCallback?: () => void;
  offset?: [number, number];
  animationType?: 'arc' | 'linear';
  scale?: number;
  duration?: number;
  startScale?: number;
}

export const FlyingImg: FC<FlyingImg> = ({
  fromRef,
  toRef,
  isFlying,
  setIsFlying,
  type,
  delay,
  completeCallback,
  offset,
  animationType,
  scale,
  duration,
  startScale,
}) => {
  const [coords, setCoords] = useState<{
    from: DOMRect;
    to: DOMRect | RectReadOnly;
  } | null>(null);

  useEffect(() => {
    if (!isFlying || !fromRef?.current || !toRef?.current) return;

    const from = fromRef.current.getBoundingClientRect();
    const to = toRef.current.getBoundingClientRect();

    setCoords({ from, to });
  }, [isFlying]);

  const typeImg = imageKeys.includes(type)
    ? images[type as ImageType]
    : getImage({ name: type, field: 'icon' });

  if (!coords || !isFlying) return null;

  const startX = coords.from.left + (offset?.[0] ?? 0);
  const startY = coords.from.top + (offset?.[1] ?? 0);

  const { leftFrames, topFrames } = generateDetailedArc({
    x0: startX,
    y0: startY,
    x1: coords.to.left,
    y1: coords.to.top,
  });

  return (
    <motion.img
      src={typeof typeImg === 'string' ? typeImg : typeImg?.img}
      initial={{
        position: 'fixed',
        top: startY,
        left: startX,
        width: coords.from.width,
        height: coords.from.height,
        zIndex: 1000,
        opacity: 0,
      }}
      animate={{
        top:
          animationType === 'linear'
            ? coords.to.top
            : [startY, ...topFrames, coords.to.top],
        left:
          animationType === 'linear'
            ? coords.to.left
            : [startX, ...leftFrames, coords.to.left],
        width: coords.to.width,
        height: coords.to.height,
        opacity: 1,
        scale: startScale && scale ? [startScale, scale] : scale,
      }}
      transition={{
        duration,
        ease: 'easeInOut',
        delay,
        opacity: { duration: 0.05, delay },
      }}
      onAnimationComplete={() => {
        setIsFlying?.(false);
        completeCallback?.();
      }}
    />
  );
};
